import React, { useState } from 'react';
import { Typography, Popper, Paper, Box, styled } from '@mui/material';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectProducts } from '../../store/product/ProductSelectors';
import { ProductSequenceData } from '../../store/product-list/ProductListTypes';

const SelectedProductsContainer = styled(Box)({
  position: 'relative',
  cursor: 'pointer',
});

const ProductTooltip = styled(Paper)({
  padding: '8px',
  width: '400px',
  maxWidth: '400px',
  maxHeight: '500px',
  overflowY: 'auto',
  zIndex: 1300,
});

const ProductItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '4px 0',
});

const ProductImage = styled('img')({
  width: '60px',
  height: '100px',
  objectFit: 'cover',
  flexShrink: 0,
});

const ProductCode = styled(Typography)({
  fontSize: '14px',
});

interface SelectProductsProps {
  selectedItems: ProductSequenceData[];
}

const SelectProducts: React.FC<SelectProductsProps> = ({ selectedItems }) => {
  const intl = useIntl();
  const allProductsData = useSelector(selectProducts);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  if (selectedItems.length === 0) return null;

  return (
    <SelectedProductsContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Typography variant="heading3_regular" noWrap>
        {selectedItems.length}{" "}
        {intl.formatMessage(
          {
            id: selectedItems.length === 1 
              ? "productListSwitch.productSelectedText" 
              : "productListSwitch.productsSelectedText",
            defaultMessage: selectedItems.length === 1 
              ? "selected product" 
              : "selected products"
          }
        )}
      </Typography>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom"
      >
        <ProductTooltip>
          {selectedItems
            .map(item => {
              const product = Object.values(allProductsData).find(p => p.productId === item.productId);
              return product ? (
                <ProductItem key={product.productId}>
                  <ProductImage
                    src={product.thumbnail}
                    alt={product.name}
                  />
                  <ProductCode variant="body2">
                    {product.code}
                  </ProductCode>
                </ProductItem>
              ) : null;
            })}
        </ProductTooltip>
      </Popper>
    </SelectedProductsContainer>
  );
};

export default SelectProducts; 